import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { computed } from 'mobx'
import type { JobWidgetStore } from '@/features/jobs/job-widget/JobWidgetStore'
import { task } from 'mobx-task'
import type { GenerateSelfServiceMagicLink } from '@/features/iam'
import type { GetExternalUrlDelegate } from '@/features/shell/platform/types'

export class JobWidgetViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  @computed
  get jobWidget() {
    return this._jobWidgetStore.jobWidget
  }

  @computed get isLoading() {
    return this._fetchJob.pending
  }

  constructor(
    private readonly _jobWidgetStore: JobWidgetStore,
    private readonly _generateSelfServiceMagicLink: GenerateSelfServiceMagicLink,
    private readonly _getExternalUrlDelegate: GetExternalUrlDelegate,
  ) {
    this._lifecycle.manage(this._jobWidgetStore.jobWidgetEvents$)
  }

  async activate(): Promise<void> {
    this._lifecycle.activate()
    const signal = this._lifecycle.signal
    await this._fetchJob(signal)
  }

  deactivate(): void {
    this._lifecycle.deactivate()
  }

  /**
   * Generates a Magic Link and navigates to it in a new window.
   */
  openPortal = task.resolved(async () => {
    const handle = this._getExternalUrlDelegate().createHandle()
    const url = await this._generateSelfServiceMagicLink(
      this.jobWidget.workspaceId,
      this.jobWidget.id
        ? {
            type: 'JobDetails',
            jobId: this.jobWidget.id,
          }
        : { type: 'Dashboard' },
    )

    handle.open(url)
  })

  private _fetchJob = task(async (signal: AbortSignal) => {
    await this._jobWidgetStore.fetchJob(signal)
  })
}
